import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { Label, TaskDetail, TaskPriority, TaskSize, TaskStatus } from '@witty-board/shared'
import { useProjectsQuery } from '../../api/projects.js'
import { useUsersQuery } from '../../api/users.js'
import { useLabelsQuery } from '../../api/labels.js'
import { useUpdateTaskMutation } from '../../api/tasks.js'
import { TASK_STATUSES } from '../../lib/board.js'
import { DeleteTaskButton } from './DeleteTaskButton.js'

interface TaskFieldsSidebarProps {
  task: TaskDetail
}

const SIZES: readonly TaskSize[] = ['XS', 'S', 'M', 'L', 'XL']
const PRIORITIES: readonly TaskPriority[] = ['low', 'normal', 'high', 'urgent']

const selectClass =
  'h-8 rounded-card border border-border bg-surface-hi px-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-meta text-text-muted">{label}</span>
      {children}
    </label>
  )
}

function LabelsField({
  task,
  labels,
  onToggle,
}: {
  task: TaskDetail
  labels: Label[]
  onToggle: (labelId: string) => void
}) {
  const { t } = useTranslation()

  if (labels.length === 0) {
    return <p className="text-meta text-text-muted">{t('task.fields.noLabels')}</p>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((label) => {
        const active = task.labelIds.includes(label.id)
        return (
          <button
            key={label.id}
            type="button"
            onClick={() => onToggle(label.id)}
            aria-pressed={active}
            className={`flex items-center gap-1 rounded-card border px-2 py-1 text-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
              active ? 'border-transparent bg-surface-hi text-text' : 'border-border text-text-muted hover:bg-surface-hi'
            }`}
          >
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: label.color }} />
            {label.name}
          </button>
        )
      })}
    </div>
  )
}

/** Columna derecha del panel: proyecto, asignado, estado, talla, prioridad, fecha, etiquetas (CLAUDE.md §6). */
export function TaskFieldsSidebar({ task }: TaskFieldsSidebarProps) {
  const { t } = useTranslation()
  const projects = useProjectsQuery()
  const users = useUsersQuery()
  const labels = useLabelsQuery(task.projectId)
  const update = useUpdateTaskMutation(task.id)

  function toggleLabel(labelId: string): void {
    const next = task.labelIds.includes(labelId)
      ? task.labelIds.filter((id) => id !== labelId)
      : [...task.labelIds, labelId]
    update.mutate({ labelIds: next })
  }

  return (
    <div className="flex flex-col gap-4">
      <Field label={t('task.fields.project')}>
        <select
          value={task.projectId}
          onChange={(event) => update.mutate({ projectId: event.target.value })}
          className={selectClass}
        >
          {projects.data?.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('task.fields.assignee')}>
        <select
          value={task.assigneeId ?? ''}
          onChange={(event) => update.mutate({ assigneeId: event.target.value || null })}
          className={selectClass}
        >
          <option value="">{t('common.unassigned')}</option>
          {users.data?.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('task.fields.status')}>
        <select
          value={task.status}
          onChange={(event) => update.mutate({ status: event.target.value as TaskStatus })}
          className={selectClass}
        >
          {TASK_STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`board.columns.${status}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('task.fields.size')}>
        <select
          value={task.size ?? ''}
          onChange={(event) => update.mutate({ size: (event.target.value || null) as TaskSize | null })}
          className={selectClass}
        >
          <option value="">{t('common.noSize')}</option>
          {SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('task.fields.priority')}>
        <select
          value={task.priority}
          onChange={(event) => update.mutate({ priority: event.target.value as TaskPriority })}
          className={selectClass}
        >
          {PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {t(`task.priority.${priority}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('task.fields.dueDate')}>
        <input
          type="date"
          value={task.dueDate ?? ''}
          onChange={(event) => update.mutate({ dueDate: event.target.value || null })}
          className={selectClass}
        />
      </Field>

      <Field label={t('task.fields.labels')}>
        <LabelsField task={task} labels={labels.data ?? []} onToggle={toggleLabel} />
      </Field>

      <div className="mt-2 border-t border-border pt-4">
        <DeleteTaskButton task={task} />
      </div>
    </div>
  )
}
